/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApolloClient, gql } from 'apollo-boost';
import { removeConnections } from 'shared/apollo';

const query = ({
  top,
  offset,
  sortBy,
  filters,
}: {
  top?: string | number;
  offset?: string | number;
  sortBy?: string;
  filters?: { [id: string]: string | boolean }[];
}) => gql`
  query useQuery(
    $top: Int
    $offset: Int
    $sortBy: String
    ${filters ? '$filters: [AccountsFilter!]' : ''}
  ) {
    adminAccounts(
      first: $top
      offset: $offset
      sortBy: $sortBy
      ${filters ? 'filters: {and: $filters}' : ''}
    ) {
      edges {
        node {
          dbId
          name
          type
          officeLocation
          ein
          website
          missionStatement
          woman
          personOfColor
          numberOfWomen
          numberOfpeopleOfColor
          totalNumber
          fiscalYearStart
          fiscalYearEnd
          orgYear
          funders
          grantees
          users {
              edges {
                  node {
                    dbId
                    firstName
                    lastName
                    email
                    title
                    phone
                    title
                    enabled
                    pronoun
                    userType
                    isLocked
                    isVerified
                  }
              }
          }
        }
      }
    }
  }
`;

export const editAccountMutation = gql`
  mutation adminUpdateAccountMutation(
    $id: Int!
    $name: String
    $type: String
    $ein: String
    $website: String
    $missionStatement: String
    $officeLocation: String
    $locationLat: Float
    $locationLng: Float
    $woman: Boolean
    $logoFileId: Int
    $personOfColor: Boolean
    $numberOfWomen: Int
    $numberOfpeopleOfColor: Int
    $totalNumber: Int
    $fiscalYearStart: Date
    $fiscalYearEnd: Date
    $orgYear: Int
  ) {
    adminUpdateAccountMutation(
      id: $id
      name: $name
      type: $type
      ein: $ein
      website: $website
      missionStatement: $missionStatement
      officeLocation: $officeLocation
      locationLat: $locationLat
      locationLng: $locationLng
      woman: $woman
      logoFileId: $logoFileId
      personOfColor: $personOfColor
      numberOfWomen: $numberOfWomen
      numberOfpeopleOfColor: $numberOfpeopleOfColor
      totalNumber: $totalNumber
      fiscalYearStart: $fiscalYearStart
      fiscalYearEnd: $fiscalYearEnd
      orgYear: $orgYear
    ) {
      success
    }
  }
`;

export const mergeAccountsMutation = gql`
  mutation adminMergeAccountsMutation($main: Int!, $secondary: Int!) {
    adminMergeAccountsMutation(main: $main, secondary: $secondary) {
      success
    }
  }
`;

export const disableUserMutation = gql`
  mutation disableUser($id: Int!) {
    disableUser(id: $id) {
      success
    }
  }
`;

export const adminUpdateUserMutation = gql`
  mutation adminUpdateUser(
    $id: Int!
    $firstName: String
    $lastName: String
    $phone: String
    $title: String
    $pronoun: String
  ) {
    adminUpdateUser(
      id: $id
      firstName: $firstName
      lastName: $lastName
      phone: $phone
      title: $title
      pronoun: $pronoun
    ) {
      success
    }
  }
`;

export const newUserMutation = gql`
  mutation newUser(
    $accountId: Int
    $email: String!
    $firstName: String!
    $lastName: String!
    $userType: String!
    $title: String
    $pronoun: String
    $password: String
  ) {
    newUser(
      email: $email
      firstName: $firstName
      lastName: $lastName
      userType: $userType
      accountId: $accountId
      title: $title
      pronoun: $pronoun
      password: $password
    ) {
      success
      userId
      error
    }
  }
`;

export const unlockUserMutation = gql`
  mutation adminUnlockUser($id: Int!, $password: String) {
    adminUnlockUser(id: $id, password: $password) {
      success
      error
    }
  }
`;

export const resendUserInvitationMutation = gql`
  mutation resendUserInvitation(
    $email: String!
    $firstName: String!
    $lastName: String!
    $accountId: Int
  ) {
    resendUserInvitation(
      email: $email
      firstName: $firstName
      lastName: $lastName
      accountId: $accountId
    ) {
      success
      error
      method
    }
  }
`;

const accountSearchQuery = gql`
  query accountSearch($search: String!, $top: Int) {
    adminAccounts(first: $top, sortBy: "NAME_ASC", filters: { and: [{ enabled: true }, { search: $search }] }) {
      edges {
        node {
          dbId
          name
          type
          ein
        }
      }
    }
  }
`;

export const verifyUserMutation = gql`
  mutation adminVerifyUser($id: Int!) {
    adminVerifyUser(id: $id) {
      success
      error
    }
  }
`;

export const getAccounts = (
  client: ApolloClient<object>,
  top?: string | number,
  offset?: string | number,
  sortBy?: string,
  search?: string,
  filterType?: string,
  addedFilters?: { [id: string]: string | boolean }[],
) => {
  const filtersArray: { [id: string]: string | boolean }[] = addedFilters
    ? [...addedFilters, { enabled: true }]
    : [{ enabled: true }];
  if (search) filtersArray.push({ search: search });
  if (filterType && filterType !== 'Any')
    filtersArray.push({ type: filterType });
  const filters = filtersArray.length > 0 ? filtersArray : undefined;
  const variables = {
    top,
    offset,
    sortBy,
    filters,
  };
  return client.query({
    query: query(variables),
    variables,
    fetchPolicy: 'network-only',
  });
};

export const editAccounts = async (
  client: ApolloClient<object>,
  values: any,
) => {
  return client
    .mutate({
      mutation: editAccountMutation,
      variables: {
        ...values,
      },
    })
    .catch(() => {
      console.warn('Unable to save the changes, please try again.');
    });
};

export const mergeAccounts = async (
  client: ApolloClient<object>,
  values: any,
) => {
  return await client
    .mutate({
      mutation: mergeAccountsMutation,
      variables: {
        ...values,
      },
    })
    .catch(() => {
      console.warn('Unable to save the changes, please try again.');
    });
};

export const searchAccounts = async (
  client: ApolloClient<object>,
  search: string,
  top = 20,
) => {
  const result = await client.query({
    query: accountSearchQuery,
    variables: { search, top },
    fetchPolicy: 'network-only',
  });
  return removeConnections(result?.data)?.adminAccounts ?? [];
};

export const disableUser = async (
  client: ApolloClient<object>,
  values: any,
) => {
  await client
    .mutate({
      mutation: disableUserMutation,
      variables: {
        ...values,
      },
    })
    .catch(() => {
      console.warn('Unable to save the changes, please try again.');
    });
};

export const updateUser = async (client: ApolloClient<object>, values: any) => {
  await client
    .mutate({
      mutation: adminUpdateUserMutation,
      variables: {
        ...values,
      },
    })
    .catch(() => {
      console.warn('Unable to save the changes, please try again.');
    });
};

const runMutation = async (
  client: ApolloClient<object>,
  mutation: any,
  values: any,
  resultKey: string,
): Promise<{ success: boolean; error?: string; data?: any }> => {
  try {
    const result = await client.mutate({
      mutation,
      variables: { ...values },
    });
    const payload = result?.data?.[resultKey];
    return {
      success: !!payload?.success,
      error: payload?.error,
      data: payload,
    };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
};

export const newUser = async (client: ApolloClient<object>, values: any) =>
  runMutation(client, newUserMutation, values, 'newUser');

export const unlockUser = async (client: ApolloClient<object>, values: any) =>
  runMutation(client, unlockUserMutation, values, 'adminUnlockUser');

export const verifyUser = async (client: ApolloClient<object>, values: any) =>
  runMutation(client, verifyUserMutation, values, 'adminVerifyUser');

export const resendUserInvitation = async (
  client: ApolloClient<object>,
  values: any,
) =>
  runMutation(
    client,
    resendUserInvitationMutation,
    values,
    'resendUserInvitation',
  );
